import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Grid2, Stack } from '@mui/material'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import * as R from 'remeda'
import { ModelListRowModel, ModelPhoto, PhotosToSave } from '../types'
import {
  buildPhotoFilename,
  type DeleteModelPhotoInput,
  type UploadModelPhotoInput,
} from '~/supabase/bucket/api'
import { generatePhotoUrl } from '~/utils/shared'
import { useUploadModelPhoto } from '../hooks/useUploadModelPhoto'
import { useRenameModelPhoto } from '../hooks/useRenameModelPhoto'
import { useDeleteModelPhoto } from '../hooks/useDeleteModelPhoto'
import { PlaceholderPhoto } from './PlaceholderPhoto'
import { SortablePhoto } from './SortabePhoto'
import { usePatchModels } from '../hooks/usePatchModels'
import { SimpleDialog } from '~/ui/components/SimpleDialog'
import { extractImageIndexFromPath } from '../../utils'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { GET_BUCKET_PHOTOS_QUERY_KEY } from '../hooks/useBucketPhotos'

const MAX_COLUMN_SIZE = 6
const ROW_SIZE = 2

export const ModelPhotos = ({
  data,
  setPhotosToSave,
}: {
  data: ModelListRowModel
  setPhotosToSave: Dispatch<SetStateAction<PhotosToSave>>
}) => {
  const uploadModelPhoto = useUploadModelPhoto()
  const renameModelPhoto = useRenameModelPhoto()
  const deleteModelPhoto = useDeleteModelPhoto()

  const { mutate: updateModel, isPending: isUpdatingModel } = usePatchModels()
  const queryClient = useQueryClient()

  const { images } = data

  const supplierCode = data.supplier_code
  const material = data.material

  const inputRef = useRef<HTMLInputElement | null>(null)

  const photos = useMemo<ModelPhoto[]>(() => {
    return R.pipe(
      images ?? [],
      R.map(({ fileName }) => {
        const index = extractImageIndexFromPath(fileName)
        if (!index) {
          toast.error(`Invalid image index for : '${fileName}'`, {
            id: 'invalid-image-index',
          })
          return null
        }
        return {
          storagePath: fileName,
          index,
        }
      }),
      R.filter(R.isNonNull),
      R.sortBy((a) => a.index)
    )
  }, [images])

  const [reorderedPhotos, setReorderedPhotos] = useState<ModelPhoto[] | null>(null)

  const photosKeyRef = useRef('')
  useEffect(() => {
    const key = photos.map((p) => p.storagePath).join(',')
    if (key === photosKeyRef.current) return
    photosKeyRef.current = key
    setReorderedPhotos(null)
  }, [photos])

  const displayPhotos = reorderedPhotos ?? photos

  const [photoPendingDelete, setPhotoPendingDelete] = useState<ModelPhoto | null>(null)

  const sensors = useSensors(useSensor(PointerSensor))

  const isMutating =
    uploadModelPhoto.isPending || deleteModelPhoto.isPending || renameModelPhoto.isPending

  const buildPhotoUrl = (storagePath: string) =>
    generatePhotoUrl({
      supplierCode,
      material,
      fileName: storagePath,
      size: 'small',
    })

  const handleUploadClick = () => {
    inputRef.current?.click()
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const nextIndex = photos.reduce((acc, photo) => Math.max(acc, photo.index), 0) + 1
    const payload: UploadModelPhotoInput = {
      supplierCode,
      material,
      index: nextIndex,
      file,
    }
    const shouldupdate = data.status_v2 === 'incomplete'

    await uploadModelPhoto.mutateAsync(payload, {
      onSuccess: () => {
        updateModel([shouldupdate ? { id: data.id, status_v2: 'ready' } : { id: data.id }])
        queryClient.invalidateQueries({ queryKey: [GET_BUCKET_PHOTOS_QUERY_KEY] })
      },
    })
  }

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return

      const activeIndex = Number(active.data.current?.sortable.index)
      const overIndex = Number(over.data.current?.sortable.index)

      if (!(typeof activeIndex === 'number' && typeof overIndex === 'number')) return

      const newOrder = arrayMove(displayPhotos, activeIndex, overIndex)
      setReorderedPhotos(newOrder)

      const originalKey = photos.map((p) => p.index).join(',')
      const newKey = newOrder.map((p) => p.index).join(',')

      if (originalKey !== newKey) {
        setPhotosToSave(
          newOrder.map((el, i) => ({
            oldPath: el.storagePath,
            newPath: buildPhotoFilename({ supplierCode, index: i + 1 }),
          }))
        )
      } else {
        setPhotosToSave(null)
      }
    },
    [displayPhotos, photos, setPhotosToSave, supplierCode]
  )

  const handleDelete = (photo: ModelPhoto) => {
    setPhotoPendingDelete(photo)
  }

  const confirmDelete = async () => {
    if (!photoPendingDelete) return

    const payload: DeleteModelPhotoInput = {
      supplierCode,
      material,
      filename: photoPendingDelete.storagePath,
    }

    const shouldUpdateModelStatus = photos.length === 1
    deleteModelPhoto.mutateAsync(payload, {
      onSuccess: () => {
        if (shouldUpdateModelStatus) {
          updateModel([{ id: data.id, status_v2: 'incomplete' }], {
            onSuccess: () => setPhotoPendingDelete(null),
          })
        } else {
          updateModel([{ id: data.id }])
          setPhotoPendingDelete(null)
        }
        queryClient.invalidateQueries({ queryKey: [GET_BUCKET_PHOTOS_QUERY_KEY] })
      },
    })
  }

  return (
    <Stack spacing={2} useFlexGap>
      <input ref={inputRef} type="file" hidden accept="image/webp" onChange={handleFileChange} />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={displayPhotos.map((p) => p.index)} strategy={rectSortingStrategy}>
          <Grid2 container columns={MAX_COLUMN_SIZE / ROW_SIZE} spacing={1}>
            {displayPhotos.map(({ storagePath, index }) => {
              const url = buildPhotoUrl(storagePath.replace(/\.webp$/, ''))

              return (
                <Grid2 key={storagePath} size={1}>
                  <SortablePhoto
                    placeId={index}
                    photo={{ index, storagePath }}
                    url={url}
                    onDelete={handleDelete}
                    disabled={isMutating}
                  />
                </Grid2>
              )
            })}
            {MAX_COLUMN_SIZE > photos.length && (
              <Grid2 size={1}>
                <PlaceholderPhoto onClick={() => handleUploadClick()} />
              </Grid2>
            )}
          </Grid2>
        </SortableContext>
      </DndContext>

      <SimpleDialog
        title="Delete photo"
        text="Are you sure you want to delete photo ? This action cannot be undone."
        onSubmit={confirmDelete}
        onClose={() => setPhotoPendingDelete(null)}
        open={Boolean(photoPendingDelete)}
        isLoading={deleteModelPhoto.isPending || isUpdatingModel}
        color="error"
        confirmText="Delete"
      />
    </Stack>
  )
}
